import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./Form.css"; 
import { requiredFieldClass } from "../lib/requiredFieldClass";
import { applicationSuccessMessage } from "../lib/applicationSuccessMessage";

const DRAFT_KEY = "draft_holiday_work";
const CALENDAR_DATES_KEY = "calendar_holiday_work_dates";

/** Formatting YYYY-MM-DD */
const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** 今日の日付文字列（YYYY-MM-DD） */
function getToday() {
  return formatYmd(new Date());
}

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const HolidayWorkForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
  // 理由 (休日出勤)
  const REASON_OPTIONS = ["業務過多・納期対応", "障害・緊急保全", "顧客対応", "イベント・行事対応", "その他"];

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    selectedDates: [],
    workType: "FURIKAE", // "NORMAL" (通常) または "FURIKAE" (振替休日)
    substituteDates: [], // 振替日。出勤予定日と同じ日数を選ぶ
    workStartTime: "09:00",
    workEndTime: "18:00",
    breakTime: "1", 
    reasonType: "",
    reasonOther: "",
  });

  const [showCalendar, setShowCalendar] = useState(false);
  const [showSubCalendar, setShowSubCalendar] = useState(false); // 振替日用カレンダー
  
  // 出勤予定日と振替休日でカレンダーの月を別々に持つ
  // （共有すると、片方で月を動かすともう片方の表示月まで変わってしまう）
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [subCalendarMonth, setSubCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  // LocalStorage (Draft / Calendar integration)
  useEffect(() => {
    try {
      const calendarDates = localStorage.getItem(CALENDAR_DATES_KEY);
      if (calendarDates) {
        const parsed = JSON.parse(calendarDates);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormData((prev) => ({ ...prev, selectedDates: parsed.sort() }));
          localStorage.removeItem(CALENDAR_DATES_KEY);
          return;
        }
        localStorage.removeItem(CALENDAR_DATES_KEY);
      }
    } catch (_) {}

    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // 振替日が1日だけだった頃の下書きも読めるようにする
        const subs = Array.isArray(parsed.substituteDates)
          ? parsed.substituteDates
          : (parsed.substituteDate ? [parsed.substituteDate] : []);
        setFormData((prev) => ({
          ...prev,
          ...parsed,
          substituteDates: subs,
          reasonOther: (parsed.reasonOther ?? "").toString().slice(0, 30),
        }));
      }
    } catch (_) {}
  }, []);

  const toggleDate = (date) => {
    setFormData((prev) => {
      const dates = prev.selectedDates.includes(date)
        ? prev.selectedDates.filter((d) => d !== date)
        : [...prev.selectedDates, date];
      return { ...prev, selectedDates: dates.sort() };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "reasonOther") {
        return { ...prev, reasonOther: value.slice(0, 30) };
      }
      if (name === "reasonType") {
        return { ...prev, reasonType: value, reasonOther: value === "その他" ? prev.reasonOther : "" };
      }
      if (name === "workType") {
        // 「通常」に切り替えたら振替日を消す（下書きに残って後で復活するのを防ぐ）
        return { ...prev, workType: value, substituteDates: value === "FURIKAE" ? prev.substituteDates : [] };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSaveDraft = async () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    alert("一時保存しました。");
  };

  const handleDeleteDraft = async () => {
    const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
    if (!confirmDelete) return;

    setLoading(true);
    try {
      if (id) {
        await api.delete(`/api/applications/drafts/${id}/`);
      }
      localStorage.removeItem(DRAFT_KEY);
      alert("下書きを削除しました。");
      navigate("/applications");
    } catch (error) {
      console.error("下書きの削除に失敗:", error);
      alert("下書きの削除に失敗しました。もう一度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/applications");
  };

  const goAfterSubmit = async () => {
    try {
      const ci = await api.get("/company-info/");
      if (ci.data?.use_employee_schedule) {
        navigate("../schedule");
        return;
      }
    } catch (_) {}
    if (isMultiForm && onNext && !isLastForm) {
      onNext();
      return;
    }
    navigate("/applications");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.selectedDates.length === 0) {
      alert("出勤予定日を選択してください。");
      return;
    }
    if (formData.workType === "FURIKAE") {
      if (formData.substituteDates.length === 0) {
        alert("振替休日（お休みする日）を選択してください。");
        return;
      }
      if (formData.substituteDates.length !== formData.selectedDates.length) {
        alert(
          `出勤予定日 ${formData.selectedDates.length} 日に対して、振替休日が ${formData.substituteDates.length} 日です。\n同じ日数を選択してください。`
        );
        return;
      }
    }
    if (!formData.reasonType) {
      alert("理由を選択してください。");
      return;
    }
    if (formData.reasonType === "その他") {
      const other = (formData.reasonOther || "").trim();
      if (!other) {
        alert("「その他」を選択した場合は理由を入力してください。");
        return;
      }
    }
    if (!window.confirm("休日出勤申請を送信してもよろしいですか？")) return;

    const effectiveReason = formData.reasonType === "その他" ? formData.reasonOther.trim() : formData.reasonType;

    try {
      await api.post("/forms/submit_holiday_work/", {
        selectedDates: formData.selectedDates,
        isFurikae: formData.workType === "FURIKAE",
        substituteDates: formData.workType === "FURIKAE" ? formData.substituteDates : [],
        workStartTime: formData.workStartTime,
        workEndTime: formData.workEndTime,
        breakTime: formData.breakTime,
        reason: effectiveReason,
      });
      localStorage.removeItem(DRAFT_KEY);
      alert(
        applicationSuccessMessage("休日出勤申請が正常に提出されました。", [
          "申請一覧から内容・状態を確認できます。",
        ])
      );
      await goAfterSubmit();
    } catch (error) {
      if (error.response?.data) {
        alert(typeof error.response.data === "string" ? error.response.data : JSON.stringify(error.response.data));
      } else {
        alert("送信に失敗しました。もう一度お試しください。");
      }
    }
  };

  const canSubmit = useMemo(
    () =>
      Boolean(
        formData.selectedDates.length > 0 &&
          (formData.workType !== "FURIKAE" ||
            formData.substituteDates.length === formData.selectedDates.length) &&
          formData.reasonType &&
          (formData.reasonType !== "その他" || formData.reasonOther.trim())
      ),
    [formData.selectedDates, formData.workType, formData.substituteDates, formData.reasonType, formData.reasonOther]
  );

  /* ===== Render Calendar ===== */
  const calendarYear = calendarMonth.getFullYear();
  const calendarMonthIndex = calendarMonth.getMonth();

  const calendarCells = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonthIndex, 1);
    const startWeekday = firstDay.getDay();
    const daysInMonth = new Date(calendarYear, calendarMonthIndex + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(calendarYear, calendarMonthIndex, day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarYear, calendarMonthIndex]);

  const goPrevMonth = () => setCalendarMonth(new Date(calendarYear, calendarMonthIndex - 1, 1));
  const goNextMonth = () => setCalendarMonth(new Date(calendarYear, calendarMonthIndex + 1, 1));

  // 振替休日カレンダー（出勤予定日とは別の月を表示できる）
  const subCalendarYear = subCalendarMonth.getFullYear();
  const subCalendarMonthIndex = subCalendarMonth.getMonth();

  const subCalendarCells = useMemo(() => {
    const firstDay = new Date(subCalendarYear, subCalendarMonthIndex, 1);
    const startWeekday = firstDay.getDay();
    const daysInMonth = new Date(subCalendarYear, subCalendarMonthIndex + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(subCalendarYear, subCalendarMonthIndex, day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [subCalendarYear, subCalendarMonthIndex]);

  const goSubPrevMonth = () => setSubCalendarMonth(new Date(subCalendarYear, subCalendarMonthIndex - 1, 1));
  const goSubNextMonth = () => setSubCalendarMonth(new Date(subCalendarYear, subCalendarMonthIndex + 1, 1));

  const handlePickDate = (d) => {
    if (!d) return;
    toggleDate(formatYmd(d));
  };

  const handlePickSubDate = (d) => {
    if (!d) return;
    const ymd = formatYmd(d);
    setFormData((prev) => {
      const dates = prev.substituteDates.includes(ymd)
        ? prev.substituteDates.filter((x) => x !== ymd)
        : [...prev.substituteDates, ymd];
      return { ...prev, substituteDates: dates.sort() };
    });
  };

  return (
    <div className="container form-container">
      <h3>休日出勤申請</h3>
      {/*<p className="form-note">※ 休日出勤を行う日、予定時間帯、申請理由を入力してください。</p>*/}

      <form onSubmit={handleSubmit}>
        {/* 0. 休日出勤区分 (通常 / 振替休日) */}
        <div className="form-section mb-3">
          <div className="form-section-title">申請区分</div>
          <div className="holiday-work-type-group">
            
            <label className="holiday-work-radio-label">
              <input
                type="radio"
                name="workType"
                value="NORMAL"
                checked={formData.workType === "NORMAL"}
                onChange={handleInputChange}
              />
              <span>通常</span>
            </label>
            
            <label className="holiday-work-radio-label">
              <input
                type="radio"
                name="workType"
                value="FURIKAE"
                checked={formData.workType === "FURIKAE"}
                onChange={handleInputChange}
              />
              <span>振替休日</span>
            </label>
          </div>
        </div>

        {/* 1. 出勤予定日選択 (Calendar) */}
        <div className="mb-3">
          <button
            type="button"
            className="holiday-work-day-toggle"
            onClick={() => setShowCalendar((v) => !v)}
            aria-expanded={showCalendar}
          >
            <span className="form-label" style={{ marginBottom: 0 }}>出勤予定日</span>
            <span className="holiday-work-day-toggle-icon" aria-hidden="true">
              {showCalendar ? "▲" : "▼"}
            </span>
          </button>

          {showCalendar && (
            <div className="holiday-work-calendar">
              <div className="holiday-work-calendar-head">
                <button type="button" className="holiday-work-calendar-nav" onClick={goPrevMonth}>‹</button>
                <div className="holiday-work-calendar-title">
                  {calendarYear}年 {calendarMonthIndex + 1}月
                </div>
                <button type="button" className="holiday-work-calendar-nav" onClick={goNextMonth}>›</button>
              </div>

              <div className="holiday-work-calendar-weekdays">
                {WEEK_LABELS.map((w, i) => (
                  <div key={w} className={`holiday-work-calendar-weekday ${i === 0 ? "is-sun" : i === 6 ? "is-sat" : ""}`}>
                    {w}
                  </div>
                ))}
              </div>

              <div className="holiday-work-calendar-grid">
                {calendarCells.map((d, idx) => {
                  if (!d) return <div key={`empty-${idx}`} className="holiday-work-calendar-cell is-empty" />;
                  const ymd = formatYmd(d);
                  const isSelected = formData.selectedDates.includes(ymd);
                  const weekday = d.getDay();
                  return (
                    <button
                      key={ymd}
                      type="button"
                      className={[
                        "holiday-work-calendar-cell",
                        isSelected ? "is-selected" : "",
                        weekday === 0 ? "is-sun" : "",
                        weekday === 6 ? "is-sat" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => handlePickDate(d)}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="selected-dates-list mt-2">
            {formData.selectedDates.map((date) => (
              <span key={date} className="holiday-work-date-chip">
                {date}
                <button type="button" className="holiday-work-date-remove" onClick={() => toggleDate(date)}>×</button>
              </span>
            ))}
          </div>
        </div>

        

        {/* 2. 勤務予定時間 */}
        <div className="form-section mb-3">
          <div className="form-section-title">勤務予定時間</div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
            <input
              type="time"
              name="workStartTime"
              className="form-control"
              value={formData.workStartTime}
              onChange={handleInputChange}
              required
            />
            <span>〜</span>
            <input
              type="time"
              name="workEndTime"
              className="form-control"
              value={formData.workEndTime}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label className="form-label">休憩時間</label>
            <select
              name="breakTime"
              className="form-select"
              value={formData.breakTime}
              onChange={handleInputChange}
            >
              <option value="0">0時間（なし）</option>
              <option value="0.25">0.25時間（15分）</option>
              <option value="0.5">0.5時間（30分）</option>
              <option value="0.75">0.75時間（45分）</option>
              <option value="1">1.0時間（60分）</option>
              <option value="1.25">1.25時間（75分）</option>
              <option value="1.5">1.5時間（90分）</option>
              <option value="1.75">1.75時間（105分）</option>
              <option value="2">2.0時間（120分）</option>
            </select>
          </div>
        </div>

        {/* 3. 申請理由 */}
        <div className="mb-3">
          <label htmlFor="reason" className="form-label">申請理由</label>
          <select
            id="reason"
            name="reasonType"
            className={requiredFieldClass(formData.reasonType)}
            value={formData.reasonType}
            onChange={handleInputChange}
            required
          >
            <option value="">-- 選択 --</option>
            {REASON_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {formData.reasonType === "その他" && (
          <div className="mb-3">
            <label htmlFor="reasonOther" className="form-label">その他の理由（30文字まで）</label>
            <input
              type="text"
              id="reasonOther"
              name="reasonOther"
              className={requiredFieldClass(formData.reasonOther)}
              value={formData.reasonOther}
              onChange={handleInputChange}
              maxLength={30}
              required
            />
          </div>
        )}

        {/* 1.5. 振替休日選択 (FURIKAE の場合のみ表示) */}
        {formData.workType === "FURIKAE" && (
          <div className="mb-3">
            <button
              type="button"
              className="holiday-work-day-toggle"
              style={{ backgroundColor: "#fff0f5", borderColor: "#e8b4c8", color: "#b83280" }}
              onClick={() => setShowSubCalendar((v) => !v)}
              aria-expanded={showSubCalendar}
            >
              <span className="form-label" style={{ marginBottom: 0 }}>
                振替休日
              </span>
              <span className="holiday-work-day-toggle-icon" aria-hidden="true">
                {showSubCalendar ? "▲" : "▼"}
              </span>
            </button>

            {showSubCalendar && (
              <div className="holiday-work-calendar">
                <div className="holiday-work-calendar-head">
                  <button type="button" className="holiday-work-calendar-nav" onClick={goSubPrevMonth}>‹</button>
                  <div className="holiday-work-calendar-title">
                    {subCalendarYear}年 {subCalendarMonthIndex + 1}月
                  </div>
                  <button type="button" className="holiday-work-calendar-nav" onClick={goSubNextMonth}>›</button>
                </div>

                <div className="holiday-work-calendar-weekdays">
                  {WEEK_LABELS.map((w, i) => (
                    <div key={w} className={`holiday-work-calendar-weekday ${i === 0 ? "is-sun" : i === 6 ? "is-sat" : ""}`}>
                      {w}
                    </div>
                  ))}
                </div>

                <div className="holiday-work-calendar-grid">
                  {subCalendarCells.map((d, idx) => {
                    if (!d) return <div key={`empty-${idx}`} className="holiday-work-calendar-cell is-empty" />;
                    const ymd = formatYmd(d);
                    const isSelected = formData.substituteDates.includes(ymd);
                    const weekday = d.getDay();
                    return (
                      <button
                        key={ymd}
                        type="button"
                        className={[
                          "holiday-work-calendar-cell",
                          isSelected ? "is-selected" : "",
                          weekday === 0 ? "is-sun" : "",
                          weekday === 6 ? "is-sat" : "",
                        ].filter(Boolean).join(" ")}
                        style={isSelected ? { backgroundColor: "#d53f8c", borderColor: "#b83280" } : {}}
                        onClick={() => handlePickSubDate(d)}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="selected-dates-list mt-2">
              {formData.substituteDates.map((date) => (
                <span key={date} className="holiday-work-date-chip" style={{ backgroundColor: "#d53f8c" }}>
                  振替日: {date}
                  <button
                    type="button"
                    className="holiday-work-date-remove"
                    onClick={() => setFormData((prev) => ({
                      ...prev,
                      substituteDates: prev.substituteDates.filter((x) => x !== date),
                    }))}
                  >×</button>
                </span>
              ))}
            </div>
            {formData.selectedDates.length !== formData.substituteDates.length && (
              <div className="mt-2" style={{ color: "#b83280", fontSize: "0.85rem" }}>
                出勤予定日 {formData.selectedDates.length} 日に対して振替休日は {formData.substituteDates.length} 日です。
                同じ日数を選択してください。
              </div>
            )}
          </div>
        )}

        {/* Button Group */}
        <div className="button-group">
          <button type="submit" className="btn-submit" disabled={!canSubmit}>
            申請
          </button>
          <button type="button" className="btn-draft" onClick={handleSaveDraft}>
            一時保存
          </button>
          {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && (
            <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft}>
              下書削除
            </button>
          )}
          <button type="button" className="btn-back" onClick={handleBack}>
            {onBack && !isFirstForm ? "前へ" : "戻る"}
          </button>
          {isMultiForm && onNext && (
            <button type="button" className="btn-nav" onClick={onNext}>
              {isLastForm ? "完了" : "次へ"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default HolidayWorkForm;